import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { InputTextModule } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AvatarModule } from 'primeng/avatar';
import { PageToolbarComponent } from '../../components/page-toolbar/page-toolbar.component';
import { ConferenceService } from '../../services/conference.service';
import { SessionDto } from '../../dto/conference.dto';
import {
  QueryResult,
  QueryResultItem,
} from '../../components/common-dto/query.dto';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

@Component({
  selector: 'app-session-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InputTextModule,
    TableModule,
    ProgressSpinnerModule,
    AvatarModule,
    PageToolbarComponent,
  ],
  template: `
    <pb-page-toolbar
      [header]="'Sessions'"
      [supportsAdd]="false"
      [supportsEdit]="false"
      [isEditing]="false"
    ></pb-page-toolbar>

    <div class="sessions-container">
      <div class="search-container">
        <span class="p-input-icon-right full-width">
          <i class="pi pi-search"></i>
          <input
            type="text"
            pInputText
            [(ngModel)]="searchQuery"
            (ngModelChange)="onSearchInput()"
            placeholder="Search sessions by title, presenter, or room..."
            class="auto-width"
          />
        </span>
      </div>

      <p-table
        [value]="sessions"
        [paginator]="true"
        [rows]="10"
        [rowsPerPageOptions]="[10, 25, 50]"
        [totalRecords]="totalRecords"
        [lazy]="true"
        (onLazyLoad)="loadData($event)"
        [loading]="loading"
      >
        <ng-template pTemplate="header">
          <tr>
            <th>Title</th>
            <th>Presenter</th>
            <th>Room</th>
            <th>Date</th>
            <th>Time</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-session>
          <tr>
            <td>{{ session.title }}</td>
            <td>
              <div class="presenter-cell">
                <p-avatar
                  [image]="session.presenterAvatar"
                  [label]="getInitials(session.presenter)"
                  shape="circle"
                  size="normal"
                  styleClass="presenter-avatar"
                ></p-avatar>
                <span class="presenter-name">{{ session.presenter }}</span>
              </div>
            </td>
            <td>{{ session.room }}</td>
            <td>{{ formatDate(session.date) }}</td>
            <td>{{ session.time }}</td>
          </tr>
        </ng-template>
        <ng-template pTemplate="emptymessage">
          <tr>
            <td colspan="5" class="text-center">No sessions found</td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  `,
  styles: [
    `
      .sessions-container {
        padding: 1rem;
      }

      .search-container {
        margin-bottom: 1.5rem;
      }

      .full-width {
        width: 100%;
        display: block;
      }

      .auto-width {
        width: 100%;
      }

      .presenter-cell {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .presenter-avatar {
        flex-shrink: 0;
      }

      .presenter-name {
        white-space: nowrap;
      }

      :host ::ng-deep .p-input-icon-right {
        width: 100%;
      }

      :host ::ng-deep .p-input-icon-right input {
        width: 100%;
      }

      :host ::ng-deep .presenter-avatar {
        width: 2rem;
        height: 2rem;
      }
    `,
  ],
})
export class SessionListComponent implements OnInit {
  sessions: SessionDto[] = [];
  searchQuery: string = '';
  loading = true;
  totalRecords = 0;
  conferenceId: string = '';

  private searchSubject = new Subject<string>();

  constructor(
    private conferenceService: ConferenceService,
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.conferenceId = this.route.snapshot.params['conferenceId'] || '';

    // Setup debounced search
    this.searchSubject
      .pipe(debounceTime(400), distinctUntilChanged())
      .subscribe(() => {
        this.loadData({ first: 0, rows: 10 });
      });

    this.loadData({ first: 0, rows: 10 });
  }

  onSearchInput(): void {
    this.searchSubject.next(this.searchQuery);
  }

  loadData(event: any): void {
    this.loading = true;
    const skip = event.first || 0;
    const take = event.rows || 10;

    const queryParams: any = {
      skip,
      take,
    };

    if (this.searchQuery && this.searchQuery.trim() !== '') {
      queryParams.filter = this.searchQuery.trim();
    }

    this.conferenceService
      .getSessions(this.conferenceId, queryParams)
      .subscribe({
        next: (result: QueryResult<SessionDto>) => {
          this.sessions = result.items.map((item) => item.item);
          this.totalRecords = result.total;
          this.loading = false;
        },
        error: (error) => {
          console.error('Error loading sessions:', error);
          this.loading = false;
        },
      });
  }

  formatDate(date: Date): string {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  getInitials(name: string): string {
    if (!name) return '';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  }
}
